import { createContext, PropsWithChildren, useContext, useMemo } from "react";
import type { User } from "firebase/auth";
import { useAuth } from "./useAuth";
import { useProfile } from "./useProfile";
import type { ProfileLoadError } from "./useProfile";
import type { UserProfile } from "../types/models";
import { deriveStartupStatus, type StartupStatus } from "../lib/startupStatus";
export type { StartupStatus } from "../lib/startupStatus";

type StartupContextValue = {
  user: User | null;
  profile: UserProfile | null;
  status: StartupStatus;
  profileError: ProfileLoadError | null;
  retry: () => void;
};

const StartupContext = createContext<StartupContextValue | null>(null);

export function StartupProvider({ children }: PropsWithChildren) {
  const { user, loading: authLoading } = useAuth();
  const verifiedUser = user?.emailVerified ? user : null;
  const { profile, loadingProfile, error: profileError, retry } = useProfile(verifiedUser);

  const status: StartupStatus = deriveStartupStatus({
    authLoading,
    user,
    loadingProfile,
    profileError,
    profile,
  });

  const value = useMemo(
    () => ({ user, profile, status, profileError, retry }),
    [profile, profileError, retry, status, user]
  );

  return <StartupContext.Provider value={value}>{children}</StartupContext.Provider>;
}

export function useStartup() {
  const value = useContext(StartupContext);
  if (!value) {
    throw new Error("useStartup must be used inside StartupProvider");
  }
  return value;
}
